/**
 * widget 会话层：身份、HTTP、实时连接与（下一批）消息收发。
 *
 * 约束来源：CONTRACT.md 第 19/22/29/31/41/42 行与需求 6/7 节。
 *
 * 安全口径（每条都有对应测试）：
 * - 源令牌只经 POST /auth/exchange 的**请求体**交给后端，support 令牌只留在内存
 *   （MemoryTokenStore），绝不写 localStorage / 不进 URL / 不进状态输出；
 * - 身份变化（登录/切换/logout）先推进 epoch 并清理：关 WS、丢弃会话与其在飞写入、
 *   清令牌/草稿/消息/上下文（账套账期回到"未核实"）；旧 epoch 的异步响应一律丢弃，
 *   不得污染新身份；
 * - 只有 customer 身份可用于悬浮窗：staff 令牌直接拒绝且不留下令牌；
 * - 发送时原子捕获上下文快照，重试复用同一 clientMessageId 与同一快照（由
 *   ConversationSession 保证）；已读只以服务端确认的客服游标为准。
 * - 宿主上下文所有权：pageInstanceId 与 version 的唯一权威是宿主。widget 只按
 *   "同实例严格递增、换实例显式隔离"接受宿主帧，绝不替宿主自增版本；
 *   widget 本地字段不拥有版本，也不替宿主上报（详见 applyHostContext / setContext）。
 */

import type {
  HostContextPayload,
  SupportPageContext,
  SupportPageContextFields,
  SupportPrincipalRef,
  WidgetEventPayload,
  WidgetStatusPayload,
} from '@support/sdk';
import { PageContextState } from '@support/sdk';
import type {
  AccountAssist,
  AccountAssistAction,
  AnswerSnapshot,
  Conversation,
  PresenceState,
  ConversationTransport,
  MessageQuote,
  PendingSend,
  PendingSendHandle,
  RealtimeStatus,
  SupportAttachment,
  SupportApi,
  SupportPrincipal,
} from '@support/client';
import {
  isAccountAssistOpen,
  ConversationSession,
  ConversationPresence,
  RealtimeClient,
  createConversationTransport,
  toSupportError,
} from '@support/client';
import type { Unsubscribe } from '@support/platform';
import {
  decideVersion,
  DraftStore,
  isCanonicalDecimal,
  MemoryTokenStore,
  newRequestId,
  TypedEmitter,
} from '@support/platform';

import type { HandshakeOutcome, WidgetBridgeHandlers } from './bridge';

/** 实时不可用时 HTTP 兜底补拉间隔。 */
const FALLBACK_POLL_MS = 5000;

/** 窗口关闭时检查新消息的间隔：页面在前台 15 秒，在后台 60 秒。 */
const WATCH_VISIBLE_MS = 15_000;
const WATCH_HIDDEN_MS = 60_000;

/**
 * “有未结束会话”提示：页面刷新后据此决定是否继续检查新消息，否则刷新后收不到提醒。
 * 只存到期时间，不含令牌、身份或会话 ID；会话结束、退出或到期即清除。
 */
const WATCH_HINT_KEY = 'support.widget.watch';

/** 窗口开着但连续这么久没有对话（收发消息或客户操作），按关闭处理：断开长连接，改为检查新消息。 */
const IDLE_SUSPEND_MS = 5 * 60 * 1000;
const WATCH_HINT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** 客服窗口的一张答案卡：标题即问题，正文即答案（Markdown，可含示意图）。 */
export interface HelpCard {
  body: string;
  id: string;
  title: string;
}

/** 客服窗口的常见问题：新对话取前几个标题作为“猜你想问”（优先当前页面常被问到的）。 */
export interface HelpHome {
  faq: HelpCard[];
}

/** 读取常见问题、按页面排序时上报的页面字段上限，与服务端一致。 */
const HELP_PAGE_FIELD_MAX = 128;
/** 会话结束后的满意度评价（本轮）。 */
export interface ConversationRating {
  comment: string;
  rated: boolean;
  score: number;
}

export type WidgetSessionState =
  | 'authenticated'
  | 'authenticating'
  | 'error'
  | 'idle'
  | 'unauthenticated';

/** 实时客户端的最小接口（真实实现是 @support/client 的 RealtimeClient）。 */
export interface WidgetRealtime {
  markDirty: () => void;
  readonly status: RealtimeStatus;
  start: () => Promise<void>;
  stop: () => void;
}

export interface WidgetRealtimeHandlers {
  onAuthExpired: () => void;
  onReady: () => Promise<void> | void;
  onStatusChange: (status: RealtimeStatus) => void;
  onSyncRequired: () => Promise<void> | void;
}

export type WidgetRealtimeFactory = (handlers: WidgetRealtimeHandlers) => WidgetRealtime;

export interface WidgetSessionOptions {
  api: SupportApi;
  /** 初始页面字段（pageKey 必填）；宿主上报的真实上下文随后镜像进来。 */
  context: SupportPageContextFields;
  /** 测试可注入替身；生产走真实 WS 客户端。 */
  createRealtime?: WidgetRealtimeFactory;
  drafts?: DraftStore;
  /** 内存令牌容器（可注入以便断言"令牌只在内存"）。 */
  store?: MemoryTokenStore;
  /** 实时基地址（已含 /v1/support 前缀）。 */
  wsBase: string;
}

export interface WidgetSessionEvents extends Record<string, unknown> {
  /** 账号协助（客服申请登录客户账号）的最新状态；没有未结束的为 null。 */
  assist: AccountAssist | null;
  event: WidgetEventPayload;
  /** 未完成的发送快照（sending/failed）。UI 据此渲染发送中/失败气泡与重试入口。 */
  pending: PendingSend[];
  status: WidgetStatusPayload;
}

/** 会话层内部使用的握手失败原因（bridge 会映射成协议允许的错误帧原因）。 */
function supersededOutcome(): HandshakeOutcome {
  return { message: '身份已变化，本次握手结果已失效', ok: false, reason: 'superseded' };
}

export class WidgetSession implements WidgetBridgeHandlers {
  private readonly api: SupportApi;

  private readonly createRealtime: WidgetRealtimeFactory;

  private conversation: Conversation | null = null;

  private conversationSession: ConversationSession | null = null;

  private queueCountValue = 0;

  /** 当前会话还没结束的账号协助申请或授权。 */
  private assistValue: AccountAssist | null = null;

  /** 上次读账号协助时的会话更新时间：会话有变化（系统消息）才重新读。 */
  private assistCheckedAt = '';

  private allAgentsFullValue = false;

  private queuePositionValue = 0;

  private estimatedWaitSecondsValue = 0;

  private offHoursValue = false;

  private serviceHoursValue = '';

  private queuePollTimer: ReturnType<typeof setInterval> | null = null;

  /** 实时连接未建立/重连期间的 HTTP 兜底补拉，避免网关不可用时客户看不到回复。 */
  private fallbackPollTimer: ReturnType<typeof setInterval> | null = null;

  /** 上下文上报的合并守卫：先落位句柄再启动异步体，否则脏标记会被永久吞掉。 */
  private contextPushDirty = false;

  private contextPushInFlight: null | { promise: Promise<void>; resolve: () => void } = null;

  private contextState: PageContextState;

  private readonly drafts: DraftStore;

  private readonly emitter = new TypedEmitter<WidgetSessionEvents>();

  private epoch = 0;

  private errorKindValue: undefined | string;

  private errorMessageValue: undefined | string;

  /**
   * 本身份 epoch 内已绑定的宿主页面实例。null 表示还没收到属于当前身份的真实
   * host.context：此时绝不向服务端上报上下文（绝不把 widget 占位页当成宿主页面）。
   */
  private hostPageInstanceId: null | string = null;

  /** 按实例隔离的宿主版本台账：只接受严格递增版本，旧/重复版本不得覆盖已接受值。 */
  private readonly hostVersions = new Map<string, string>();

  /** widget 本地字段（宿主未权威提供的键）：随宿主版本一起上报，冲突时宿主优先。 */
  private localFields: Partial<SupportPageContextFields> = {};

  private readonly initialContext: SupportPageContextFields;

  private openValue = false;

  private primaryState: WidgetSessionState = 'idle';

  private realtime: WidgetRealtime | null = null;

  private presence: ConversationPresence | null = null;

  private counterpartTypingValue = false;

  private aiRespondingValue = false;

  private aiStatusValue = '';

  private answerValue: AnswerSnapshot | null = null;
  private answerController: AbortController | null = null;
  private answerRetry: ReturnType<typeof setTimeout> | null = null;
  private answerDenied = false;
  private answerFailures = 0;
  private answerLive = false;
  private answerCompletionCheck: object | null = null;

  private readonly store: MemoryTokenStore;

  private readonly transport: ConversationTransport;

  private unreadValue = 0;
  private readonly wsBase: string;

  /**
   * 宿主给的源令牌，只在内存里：握手时先收下待命，客户打开窗口时才换票建立连接，
   * 窗口关闭期间凭证过期时也用它静默重新换票。
   */
  private sourceToken: null | string = null;

  /** 进行中的连接（换票、取会话、建长连接），重复打开共用同一次。 */
  private connecting: null | Promise<void> = null;

  /** 窗口关闭时检查新消息的定时器（只在有未结束会话时运行）。 */
  private watchTimer: ReturnType<typeof setTimeout> | null = null;

  /** 窗口开着但长时间没有对话，已按关闭处理（断开长连接、改为检查新消息）。 */
  private idleSuspended = false;

  private idleTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly onVisibilityChange = (): void => {
    // 切回前台视为有操作：长时间无对话而暂停的连接立即恢复。
    if (document.visibilityState === 'visible') this.noteActivity();
    this.syncPresenceActivity();
    if (this.canAcknowledge()) void this.reconcile(this.epoch);
    // 切回前台立刻检查一次，不必等后台的长间隔。
    if (this.watchTimer !== null && document.visibilityState === 'visible') this.scheduleWatch(this.epoch, 0);
  };

  constructor(options: WidgetSessionOptions) {
    this.api = options.api;
    this.wsBase = options.wsBase;
    this.initialContext = options.context;
    this.store = options.store ?? new MemoryTokenStore();
    this.drafts = options.drafts ?? new DraftStore();
    this.contextState = new PageContextState({ fields: options.context });
    this.transport = createConversationTransport(options.api);
    this.createRealtime =
      options.createRealtime ??
      ((handlers) =>
        new RealtimeClient({
          fetchTicket: () => this.api.fetchTicket(),
          onAuthExpired: handlers.onAuthExpired,
          onDraining: () => {
            void handlers.onSyncRequired();
          },
          onReady: handlers.onReady,
          onStatusChange: handlers.onStatusChange,
          onSyncRequired: () => {
            void handlers.onSyncRequired();
          },
          url: this.wsBase,
        }));
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  get connectionStatus(): RealtimeStatus | 'none' {
    return this.realtime?.status ?? 'none';
  }

  /** 当前页面上下文（快照语义：每次返回新对象）。 */
  get context(): SupportPageContext {
    return this.contextState.captureSnapshot();
  }

  get conversationId(): null | string {
    return this.conversation?.id ?? null;
  }

  get conversationStatus(): Conversation['status'] | null {
    return this.conversation?.status ?? null;
  }

  get staffReadCursor(): string {
    return this.conversation?.staffReadCursor ?? '0';
  }

  get counterpartTyping(): boolean { return this.counterpartTypingValue; }

  get assist(): AccountAssist | null { return this.assistValue; }

  get aiResponding(): boolean { return this.aiRespondingValue; }

  get answerPreview(): string {
    const answer = this.answerValue;
    if (!answer?.responding || !answer.text) return '';
    const messages = this.visibleMessages();
    const trigger = messages.findIndex((message) => message.id === answer.messageId);
    // WS 可能先于 SSE 终止事件带回最终历史，不同时显示已落库回复与预览。
    if (trigger >= 0 && messages.slice(trigger + 1).some((message) =>
      message.senderType === 'system' && message.senderId === 'ai')) return '';
    return answer.text;
  }

  /** 智能客服当前步骤说明（如“正在查询企业账期…”）。 */
  get aiStatus(): string {
    return this.aiRespondingValue ? this.aiStatusValue : '';
  }

  get queueCount(): number {
    return this.queueCountValue;
  }

  get allAgentsFull(): boolean {
    return this.allAgentsFullValue;
  }

  /** 当前客户在人工队列中的位置（从 1 开始），未排队为 0。 */
  get queuePosition(): number {
    return this.queuePositionValue;
  }

  /** 按近期实际排队时长估算的等待秒数；0 表示不展示。 */
  get estimatedWaitSeconds(): number {
    return this.estimatedWaitSecondsValue;
  }

  /** 当前是否为人工客服非工作时间，以及面向客户的工作时间说明。 */
  get offHours(): boolean {
    return this.offHoursValue;
  }

  get serviceHours(): string {
    return this.serviceHoursValue;
  }

  get draft(): string {
    return this.drafts.get(this.draftKey());
  }

  get isOpen(): boolean {
    return this.openValue;
  }

  get principal(): null | SupportPrincipalRef {
    return this.store.current?.principal ?? null;
  }

  /** widget 内部状态（含协议不表达的 authenticating，供 UI 展示"连接中"）。 */
  get state(): WidgetSessionState {
    return this.primaryState;
  }

  /** 上报宿主的协议状态：协议只有 idle/authenticated/error/unauthenticated。 */
  get status(): WidgetStatusPayload {
    return {
      open: this.openValue,
      state: toProtocolState(this.primaryState),
      ...(this.errorKindValue === undefined ? {} : { errorKind: this.errorKindValue }),
      ...(this.errorMessageValue === undefined
        ? {}
        : { errorMessage: this.errorMessageValue }),
      unread: this.unreadValue,
    };
  }

  on<K extends keyof WidgetSessionEvents>(
    type: K,
    handler: (payload: WidgetSessionEvents[K]) => void,
  ): Unsubscribe {
    return this.emitter.on(type, handler);
  }

  /**
   * 收下宿主提供的源令牌（握手或身份切换）。
   *
   * 为了不让每个在线客户都常驻长连接，这里只待命、不发任何请求：
   * - 窗口已打开：立即换票、取会话并建立长连接（与打开窗口相同）；
   * - 窗口关闭但本浏览器有未结束会话的提示：换票并定时检查新消息，用于提醒；
   * - 其他情况什么也不做，直到客户打开窗口。
   * 每次调用都会推进 epoch：被更新的身份请求取代的旧请求返回 superseded，
   * 且不得再写入任何状态（令牌/会话/上下文）。
   */
  async authenticate(sourceToken: string): Promise<HandshakeOutcome> {
    const epoch = this.beginIdentity();
    this.sourceToken = sourceToken;
    if (this.openValue) {
      await this.connect(epoch);
      if (!this.isCurrent(epoch)) return supersededOutcome();
      if (this.primaryState === 'error') {
        if (this.errorKindValue === 'identity') return { ok: false, reason: 'unauthorized' };
        return { message: this.errorMessageValue, ok: false, reason: reasonForError(this.errorKindValue ?? '') };
      }
      const principal = this.store.current?.principal;
      return { ok: true, principal: principal ? toPrincipalRef(principal) : null };
    }
    this.setState('idle');
    if (readWatchHint()) void this.resumeWatch(epoch);
    return { ok: true, principal: null };
  }

  /** 换票（已有有效令牌时跳过）；源令牌只经请求体交给后端。 */
  private async ensureToken(epoch: number): Promise<void> {
    if (this.store.token !== null) return;
    const sourceToken = this.sourceToken;
    if (sourceToken === null) throw new Error('缺少宿主登录凭据');
    const exchange = await this.api.exchange('customer', sourceToken);
    if (!this.isCurrent(epoch)) return;
    if (exchange.principal.role !== 'customer') {
      // 悬浮窗只服务客户身份：staff 令牌在这里直接拒绝
      throw Object.assign(new Error('悬浮窗只接受客户身份'), { widgetKind: 'identity' });
    }
    this.store.set({
      expiresAt: exchange.expiresAt,
      principal: exchange.principal,
      token: exchange.token,
    });
  }

  /** 打开窗口时建立连接：换票、取（首次则创建）会话、补拉消息、建长连接。 */
  private connect(epoch: number): Promise<void> {
    if (this.connecting === null) {
      const run = this.doConnect(epoch).finally(() => {
        if (this.connecting === run) this.connecting = null;
      });
      this.connecting = run;
    }
    return this.connecting;
  }

  private async doConnect(epoch: number): Promise<void> {
    if (!this.isCurrent(epoch) || this.sourceToken === null) return;
    this.stopWatch();
    const firstTime = this.conversationSession === null;
    if (firstTime || this.primaryState !== 'authenticated') this.setState('authenticating');
    try {
      await this.ensureToken(epoch);
      if (!this.isCurrent(epoch)) return;
      if (firstTime) {
        const conversation = await this.resolveConversation();
        if (!this.isCurrent(epoch)) return;
        this.adoptConversation(conversation);
        // 会话就绪：把宿主已上报的当前上下文按页面实例上报一次
        this.scheduleContextPush();
        await this.conversationSession?.loadInitial();
        if (!this.isCurrent(epoch)) return;
        await this.markReadIfPossible();
        if (!this.isCurrent(epoch)) return;
        this.setState('authenticated');
        this.emitEvent({ conversationId: this.conversation?.id, kind: 'conversation.changed' });
        void this.refreshAssist(epoch);
      } else {
        // 关闭期间可能有新消息或状态变化：补拉后再确认已读。
        await this.reconcile(epoch);
        if (!this.isCurrent(epoch)) return;
        if (this.primaryState === 'error') return;
        this.setState('authenticated');
      }
      this.noteWatchHint();
      this.syncPresenceActivity();
      // 连接期间窗口又被关上：不再排队轮询、不建长连接，改为检查新消息。
      if (!this.isLive()) {
        this.startWatch(epoch);
        return;
      }
      this.startQueuePolling(epoch);
      if (this.realtime === null) await this.startRealtime(epoch);
      if (this.isCurrent(epoch)) this.armIdle(epoch);
    } catch (error) {
      if (!this.isCurrent(epoch)) return;
      const kind = (error as { widgetKind?: string }).widgetKind ?? toSupportError(error).kind;
      if (kind === 'identity') this.store.clear();
      this.fail(kind, error instanceof Error && kind === 'identity' ? error.message : toSupportError(error).message);
    }
  }

  private adoptConversation(conversation: Conversation): void {
    this.conversation = conversation;
    this.unreadValue = conversation.unread;
    this.conversationSession = new ConversationSession({
      conversationId: conversation.id,
      role: 'customer',
      transport: this.transport,
    });
    this.replacePresence(conversation.id);
  }

  /**
   * 页面刷新后按提示恢复“检查新消息”：换票并查最近一次会话，未结束才开始检查，
   * 不创建会话、不建长连接；已结束或没有会话则清除提示。
   */
  private async resumeWatch(epoch: number): Promise<void> {
    try {
      await this.ensureToken(epoch);
      if (!this.isCurrent(epoch) || this.conversationSession !== null) return;
      const page = await this.api.listConversations({ limit: 1 });
      if (!this.isCurrent(epoch) || this.conversationSession !== null) return;
      const latest = page.items[0];
      if (!latest) {
        clearWatchHint();
        return;
      }
      if (latest.status === 'closed') {
        // 会话结束了，但客服可能还在客户账号里排查：授权没结束就继续检查，横幅照常显示。
        const assist = await this.fetchAssist(latest.id).catch(() => null);
        if (!this.isCurrent(epoch) || this.conversationSession !== null) return;
        if (!isAccountAssistOpen(assist)) {
          clearWatchHint();
          return;
        }
      }
      this.adoptConversation(latest);
      void this.refreshAssist(epoch);
      this.emitStatus();
      if (this.openValue) void this.connect(epoch);
      else this.startWatch(epoch);
    } catch {
      // 提醒是附加能力：失败时不打扰客户，打开窗口时照常连接。
    }
  }

  /** 关闭窗口：断开长连接与各类前台轮询，保留令牌与会话；有未结束会话时改为定时检查新消息。 */
  private suspend(epoch: number): void {
    this.clearIdle();
    this.stopAnswerStream();
    this.stopFallbackPoll();
    this.stopQueuePolling();
    const realtime = this.realtime;
    this.realtime = null;
    try {
      realtime?.stop();
    } catch {
      // 关闭失败不影响状态收敛
    }
    this.startWatch(epoch);
  }

  /** 长连接应当在线：窗口开着，且没有因长时间无对话而暂停。 */
  private isLive(): boolean {
    return this.openValue && !this.idleSuspended;
  }

  /**
   * 客户在窗口里的操作（输入、点击、滚动、切回前台）。重新开始计算空闲时间；
   * 若连接已因长时间无对话而暂停，立即恢复并补拉消息。
   */
  noteActivity(): void {
    if (!this.openValue || this.sourceToken === null) return;
    if (this.idleSuspended) {
      this.resumeFromIdle(this.epoch);
      return;
    }
    if (this.realtime !== null) this.armIdle(this.epoch);
  }

  private resumeFromIdle(epoch: number): void {
    this.idleSuspended = false;
    this.stopWatch();
    this.syncPresenceActivity();
    void this.connect(epoch);
  }

  private armIdle(epoch: number): void {
    this.clearIdle();
    if (!this.isLive()) return;
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      this.onIdle(epoch);
    }, IDLE_SUSPEND_MS);
  }

  private clearIdle(): void {
    if (this.idleTimer !== null) clearTimeout(this.idleTimer);
    this.idleTimer = null;
  }

  /** 空闲到时：AI 正在回答或消息还在发送时不算空闲；否则按关闭处理。 */
  private onIdle(epoch: number): void {
    if (!this.isCurrent(epoch) || !this.isLive()) return;
    if (this.aiRespondingValue || this.pendingSends().some((item) => item.status === 'sending')) {
      this.armIdle(epoch);
      return;
    }
    this.idleSuspended = true;
    this.syncPresenceActivity();
    this.suspend(epoch);
    this.emitStatus();
  }

  private startWatch(epoch: number): void {
    if (this.isLive() || this.conversationSession === null ||
      (this.conversation?.status === 'closed' && !isAccountAssistOpen(this.assistValue))) return;
    this.scheduleWatch(epoch, document.visibilityState === 'visible' ? WATCH_VISIBLE_MS : WATCH_HIDDEN_MS);
  }

  private scheduleWatch(epoch: number, delay: number): void {
    this.stopWatch();
    this.watchTimer = setTimeout(() => {
      this.watchTimer = null;
      void this.watchOnce(epoch);
    }, delay);
  }

  private stopWatch(): void {
    if (this.watchTimer !== null) clearTimeout(this.watchTimer);
    this.watchTimer = null;
  }

  /** 查一次会话的未读数与状态，更新宿主的提醒角标；凭证过期时用源令牌静默重新换票。 */
  private async watchOnce(epoch: number): Promise<void> {
    const id = this.conversation?.id;
    if (!this.isCurrent(epoch) || this.isLive() || !id) return;
    try {
      const previousStatus = this.conversation?.status;
      const updated = await this.api.getConversation(id);
      if (!this.isCurrent(epoch) || this.isLive() || this.conversation?.id !== id) return;
      this.applyConversationUpdate(updated);
      // 窗口开着、只是因长时间无对话暂停：来了新消息或状态有变，自动恢复连接并显示。
      if (this.idleSuspended && (updated.unread > 0 || updated.status !== previousStatus)) {
        this.resumeFromIdle(epoch);
        return;
      }
    } catch (error) {
      if (!this.isCurrent(epoch)) return;
      if (toSupportError(error).kind === 'unauthorized') this.store.clear();
      try {
        await this.ensureToken(epoch);
      } catch {
        // 重新换票失败（例如宿主已退出）：保持静默，下次再试。
      }
    }
    if (!this.isCurrent(epoch) || this.isLive()) return;
    if (this.conversation?.status === 'closed' && !isAccountAssistOpen(this.assistValue)) {
      clearWatchHint();
      return;
    }
    this.startWatch(epoch);
  }

  /** 有未结束会话时记下提示，刷新页面后才能继续提醒；会话已结束则清除。 */
  private noteWatchHint(): void {
    if (this.conversation && (this.conversation.status !== 'closed' || isAccountAssistOpen(this.assistValue))) writeWatchHint();
    else clearWatchHint();
  }

  /**
   * 登出：先尽力撤销服务端 support 会话（用当前令牌），再彻底清理本地状态。
   * 撤销失败不回滚本地清理——本地必须干净。
   */
  async logout(): Promise<void> {
    const hadToken = this.store.token !== null;
    this.epoch += 1;
    this.stopAnswerStream();
    if (hadToken) {
      try {
        await this.api.logout();
      } catch {
        // 服务端不可达时也必须完成本地清理
      }
    }
    this.teardown();
    this.sourceToken = null;
    clearWatchHint();
    this.setState('unauthenticated');
  }

  /**
   * 宿主上报的页面上下文（host.context 帧与握手携带的上下文走同一条路径）。
   *
   * 所有权口径（pageInstanceId 与 version 的唯一权威是宿主）：
   * - 实例与版本**原样采用**：绝不在 widget 侧为宿主自增版本，也绝不用内部计数替换宿主版本；
   * - 同实例只接受**严格递增**的宿主版本：迟到的旧版本与重复版本一律忽略
   *   （不覆盖已接受值、不产生上报），宿主版本跳跃（如 3 -> 7）原样落到出站载荷；
   * - **换实例即显式隔离重建**：只带该实例自己的字段与版本，绝不与上一个实例的字段混合；
   * - 实例标识/版本/pageKey 非法的帧直接忽略（协议要求非零规范十进制与稳定标识，
   *   非法值只会在后端 1901003，绝不用本地兜底掩盖）；
   * - 版本台账按身份 epoch 独立：身份切换时清空（服务端同样按
   *   (conversationId,customerId,pageInstanceId) 隔离，新客户不沿用旧客户版本序列）。
   *
   * `context.applied` 事件只表示 widget **本地已应用**该上下文：不带 accepted。
   * 服务端是否真的落库由随后的 POST /conversations/:id/context 应答决定，
   * 确认后单独发一条同名事件并带上服务端返回的 accepted（绝不由本地自证）。
   */
  applyHostContext(payload: HostContextPayload): void {
    const instanceId = payload.pageInstanceId;
    if (typeof instanceId !== 'string' || instanceId === '') return;
    const version = payload.version;
    if (!isCanonicalDecimal(version) || version === '0') return;
    const fields = hostContextFields(payload);
    if (fields === null) return;
    const decision = decideVersion(this.hostVersions.get(instanceId) ?? null, version);
    if (!decision.accepted) return;
    this.hostVersions.set(instanceId, decision.version);
    this.hostPageInstanceId = instanceId;
    // 宿主版本原样采用 + 换实例显式重建：绝不与另一实例的字段/版本混合
    this.contextState = new PageContextState({
      fields: this.overlayLocalFields(fields),
      pageInstanceId: instanceId,
      version: decision.version,
    });
    this.emitEvent({ kind: 'context.applied' });
    this.emitStatus();
    this.scheduleContextPush();
  }

  /**
   * widget 侧本地字段更新（例如当前标签页）。
   *
   * 版本由宿主拥有：本地字段更新**不推进版本，也不替宿主触发上报**——
   * widget 无权替宿主产生新版本，否则会把宿主真实的下一个版本挤成"重复版本"而被服务端拒绝。
   * 本地字段随宿主下一个版本的上报与消息快照一起发出；与宿主字段冲突时宿主优先，
   * 绝不改写宿主上报的账套核实口径。
   */
  setContext(partial: Partial<SupportPageContextFields>): SupportPageContext {
    const local = this.localFields as Record<string, unknown>;
    for (const [key, value] of Object.entries(partial)) {
      if (value === undefined) continue;
      local[key] = value;
    }
    const current = this.contextState.captureSnapshot();
    // 只替换字段、版本原样沿用当前（宿主拥有的）版本
    this.contextState = new PageContextState({
      fields: this.overlayLocalFields(contextFields(current)),
      pageInstanceId: current.pageInstanceId,
      version: this.contextState.version,
    });
    this.emitStatus();
    return this.contextState.captureSnapshot();
  }

  setDraft(text: string): void {
    this.drafts.set(this.draftKey(), text);
    // 智能接待、排队中也上报：服务端据此判断客户是否还在输入，超时（智能客服 5 分钟、人工 15 分钟）没输入也没发消息才自动结束；只有人工接待中坐席能看到“正在输入”。
    if (this.conversation && this.conversation.status !== 'closed' && this.canAcknowledge()) {
      this.presence?.noteInput(text.trim() !== '');
    } else {
      this.presence?.clearTyping();
    }
  }

  stopTyping(): void { this.presence?.clearTyping(); }

  /**
   * 宿主打开或关闭客服窗口。打开时建立连接（先补拉再确认已读，避免收起期间的新消息
   * 尚未加载就清空未读提示）；关闭时断开长连接，改为定时检查新消息。
   */
  setOpen(open: boolean): void {
    const changed = this.openValue !== open;
    this.openValue = open;
    this.syncPresenceActivity();
    if (open) {
      this.stopWatch();
      this.idleSuspended = false;
      this.syncPresenceActivity();
      if (this.sourceToken !== null) void this.connect(this.epoch);
    } else if (changed || this.idleSuspended) {
      this.idleSuspended = false;
      this.suspend(this.epoch);
    }
    this.emitStatus();
  }

  /**
   * 读取客服窗口的常见问题（标题用于新对话的“猜你想问”），按宿主上报的当前页面排序。
   * 未认证或身份已切换时返回 null。
   */
  async loadHelpHome(): Promise<HelpHome | null> {
    if (this.primaryState !== 'authenticated') return null;
    const epoch = this.epoch;
    const context = this.context;
    const home = await this.api.request<HelpHome>('/help/home', {
      query: {
        module: context.module?.slice(0, HELP_PAGE_FIELD_MAX),
        routeName: context.routeName?.slice(0, HELP_PAGE_FIELD_MAX),
        title: context.pageTitle?.slice(0, HELP_PAGE_FIELD_MAX),
      },
    });
    return this.isCurrent(epoch) ? home : null;
  }

  /** 页面卸载 / 宿主销毁：停实时连接并作废在飞工作。 */
  stop(): void {
    this.epoch += 1;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.teardown();
    this.setState('idle');
  }

  visibleMessages(): ReturnType<ConversationSession['visibleMessages']> {
    return this.conversationSession?.visibleMessages() ?? [];
  }

  get historyBeforeSequence(): string {
    return this.conversation?.historyBeforeSequence ?? '0';
  }

  /**
   * 发送一条客户文本消息。
   * 发送时原子捕获当前页面上下文快照；重试由 ConversationSession 复用同一
   * clientMessageId 与同一快照对象，绝不重新采集。
   */
  sendMessage(text: string, attachments: { id: string; isImage: boolean }[] = [], quote?: MessageQuote): PendingSendHandle {
    const session = this.conversationSession;
    if (!session || this.conversation?.status === 'closed') {
      throw new Error('会话尚未就绪，无法发送消息');
    }
    const handle = session.send({
      context: this.context,
      ...(quote ? { quote } : {}),
      text,
      ...(attachments.length ? {
        attachmentIds: attachments.map((item) => item.id),
        contentType: attachments.every((item) => item.isImage) ? 'image' : 'file',
      } : {}),
    });
    this.trackPending(handle);
    this.noteActivity();
    return handle;
  }

  /** 引用跳转：原消息不在已加载的历史里时向前翻页加载。 */
  async revealMessage(messageId: string): Promise<boolean> {
    const session = this.conversationSession;
    if (!session || this.primaryState !== 'authenticated') return false;
    const epoch = this.epoch;
    const before = session.visibleMessages().length;
    const found = await session.loadUntil(messageId);
    if (!this.isCurrent(epoch) || this.conversationSession !== session) return false;
    if (session.visibleMessages().length !== before && this.conversation) {
      this.emitEvent({ conversationId: this.conversation.id, kind: 'conversation.changed' });
    }
    return found;
  }

  /** 本人自选头像编号；为空时按身份默认分配。 */
  get avatar(): string {
    return (this.store.current?.principal as SupportPrincipal | undefined)?.avatar ?? '';
  }

  /** 设置本人头像（空字符串恢复默认），成功后刷新窗口里自己的头像。 */
  async setAvatar(avatar: string): Promise<void> {
    if (this.primaryState !== 'authenticated') throw new Error('尚未连接客服');
    const epoch = this.epoch;
    const updated = await this.api.request<SupportPrincipal>('/me/avatar', { body: { avatar }, method: 'POST' });
    const current = this.store.current;
    if (!this.isCurrent(epoch) || !current) return;
    this.store.set({ ...current, principal: { ...current.principal, avatar: updated.avatar ?? '' } as typeof current.principal });
    this.emitStatus();
  }

  /** 客户点击转人工后，服务端先记录请求，再进入既有排队和自动分配链路。 */
  async requestHuman(): Promise<void> {
    const current = this.conversation;
    if (!current || current.status !== 'ai' || this.primaryState !== 'authenticated') {
      throw new Error('当前会话无法转人工');
    }
    const epoch = this.epoch;
    const updated = await this.api.request<Conversation>(
      `/conversations/${encodeURIComponent(current.id)}/handoff`,
      { method: 'POST', body: { version: current.version, clientRequestId: newRequestId() } },
    );
    if (!this.isCurrent(epoch) || this.conversation?.id !== current.id) return;
    this.conversation = updated;
    this.emitStatus();
    this.emitEvent({ conversationId: updated.id, kind: 'conversation.changed' });
    await this.reconcile(epoch);
    this.startQueuePolling(epoch);
  }

  /** 客户在排队中说问题已经解决、不用人工了：取消排队，会话回到智能客服接待。 */
  async cancelHandoff(): Promise<void> {
    const current = this.conversation;
    if (!current || current.status !== 'waiting' || this.primaryState !== 'authenticated') {
      throw new Error('当前不在排队中');
    }
    const epoch = this.epoch;
    const updated = await this.api.request<Conversation>(
      `/conversations/${encodeURIComponent(current.id)}/cancel-handoff`,
      { method: 'POST', body: { version: current.version, clientRequestId: newRequestId() } },
    );
    if (!this.isCurrent(epoch) || this.conversation?.id !== current.id) return;
    this.conversation = updated;
    this.stopQueuePolling();
    this.emitStatus();
    this.emitEvent({ conversationId: updated.id, kind: 'conversation.changed' });
    await this.reconcile(epoch);
  }

  /** 读取已结束会话本轮的满意度评价；会话未结束时返回 null。 */
  async loadRating(): Promise<ConversationRating | null> {
    const current = this.conversation;
    if (!current || current.status !== 'closed' || this.primaryState !== 'authenticated') return null;
    const epoch = this.epoch;
    const rating = await this.api.request<ConversationRating>(`/conversations/${encodeURIComponent(current.id)}/rating`);
    if (!this.isCurrent(epoch) || this.conversation?.id !== current.id) return null;
    return rating;
  }

  /** 会话结束后提交本轮满意度评价（1–5 分，评语选填）。 */
  async submitRating(score: number, comment: string): Promise<ConversationRating> {
    const current = this.conversation;
    if (!current || current.status !== 'closed' || this.primaryState !== 'authenticated') {
      throw new Error('会话尚未结束，暂不能评价');
    }
    return await this.api.request<ConversationRating>(`/conversations/${encodeURIComponent(current.id)}/rating`, {
      body: { comment, score },
      method: 'POST',
    });
  }

  /** 记录客户对智能客服某条答复的“已解决/未解决”，改选时以最后一次为准。 */
  async submitAIFeedback(messageId: string, resolved: boolean): Promise<void> {
    const current = this.conversation;
    if (!current || this.primaryState !== 'authenticated') return;
    await this.api.request(`/conversations/${encodeURIComponent(current.id)}/ai-feedback`, {
      body: { messageId, resolved },
      method: 'POST',
    });
  }

  /** 客户明确继续咨询时重新打开同一条会话。 */
  async startNewConversation(): Promise<void> {
    const previous = this.conversation;
    if (!previous || previous.status !== 'closed' || this.primaryState !== 'authenticated') {
      throw new Error('当前会话尚未结束');
    }
    const epoch = this.epoch;
    const next = await this.api.createConversation(newRequestId());
    if (!this.isCurrent(epoch) || this.conversation?.id !== previous.id) return;
    const nextSession = new ConversationSession({
      conversationId: next.id,
      role: 'customer',
      transport: this.transport,
    });
    await nextSession.loadInitial();
    if (!this.isCurrent(epoch) || this.conversation?.id !== previous.id) return;
    this.drafts.set(previous.id, '');
    this.conversation = next;
    this.conversationSession = nextSession;
    this.replacePresence(next.id);
    this.noteWatchHint();
    this.syncPresenceActivity();
    this.stopQueuePolling();
    this.unreadValue = next.unread;
    this.scheduleContextPush();
    this.emitPending();
    this.emitStatus();
    this.emitEvent({ conversationId: next.id, kind: 'conversation.changed' });
    this.startQueuePolling(epoch);
  }

  /** 上传公开附件；身份切换后不得把旧上传结果用于新会话。 */
  async uploadAttachment(file: File): Promise<SupportAttachment> {
    const session = this.conversationSession;
    if (!session || this.primaryState !== 'authenticated') {
      throw new Error('会话尚未就绪，无法上传附件');
    }
    const epoch = this.epoch;
    const attachment = await session.uploadAttachment(file, 'public', file.name);
    if (!this.isCurrent(epoch) || this.conversationSession !== session) {
      throw new Error('身份已变化，本次上传结果已失效');
    }
    return attachment;
  }

  /** 下载当前客户可见消息中的附件；服务端再次核验可见范围。 */
  async downloadAttachment(attachmentId: string): Promise<Blob> {
    const session = this.conversationSession;
    if (!session || this.primaryState !== 'authenticated') {
      throw new Error('会话尚未就绪，无法下载附件');
    }
    const epoch = this.epoch;
    const visible = session.visibleMessages().some((message) =>
      message.attachments.some((attachment) => attachment.id === attachmentId));
    if (!visible) throw new Error('附件不在当前可见消息中');
    const blob = await this.api.downloadAttachment(attachmentId);
    if (!this.isCurrent(epoch) || this.conversationSession !== session) {
      throw new Error('身份已变化，本次下载结果已失效');
    }
    return blob;
  }

  /** 读取智能客服回答里的答案卡示意图；身份变化后作废。 */
  async downloadKnowledgeImage(imageId: string): Promise<Blob> {
    if (this.primaryState !== 'authenticated') throw new Error('会话尚未就绪，无法读取示意图');
    const epoch = this.epoch;
    const blob = await this.api.downloadKnowledgeImage(imageId);
    if (!this.isCurrent(epoch)) throw new Error('身份已变化，本次读取结果已失效');
    return blob;
  }

  /** 原 key 重试失败消息；会话未就绪或已在发送中时返回 null。 */
  retrySend(clientMessageId: string): null | PendingSendHandle {
    const session = this.conversationSession;
    if (!session) return null;
    const handle = session.retry(clientMessageId);
    if (!handle) return null;
    this.trackPending(handle);
    this.noteActivity();
    return handle;
  }

  /** 未完成的发送（sending/failed），供 UI 展示状态与重试入口。 */
  pendingSends(): PendingSend[] {
    return this.conversationSession?.pendingSends() ?? [];
  }

  private trackPending(handle: PendingSendHandle): void {
    const epoch = this.epoch;
    this.emitPending();
    const settle = (): void => {
      this.emitPending();
      this.startQueuePolling(epoch);
    };
    // 两个分支都要收尾：失败分支同样要通知 UI（并顺手消化 rejection）
    void handle.settled.then(() => {
      settle();
      if (!this.isCurrent(epoch)) return;
      if (this.conversation?.status === 'ai' || this.conversation?.status === 'waiting') {
        this.aiRespondingValue = true;
        this.syncAnswerStream();
        this.emitStatus();
      }
      void this.presence?.refresh();
    }, settle);
  }

  private replacePresence(conversationId: string): void {
    this.stopAnswerStream();
    this.answerDenied = false;
    this.presence?.stop();
    this.counterpartTypingValue = false;
    this.aiRespondingValue = false;
    const epoch = this.epoch;
    this.presence = new ConversationPresence(this.api, conversationId, (state: PresenceState) => {
      if (!this.isCurrent(epoch) || this.conversation?.id !== conversationId) return;
      this.counterpartTypingValue = state.counterpartTyping;
      this.aiRespondingValue = state.aiResponding || this.answerValue?.responding === true;
      this.aiStatusValue = state.aiStatus ?? '';
      if (!state.aiResponding && !this.answerLive && this.answerValue?.responding) {
        void this.checkAnswerCompletion();
      }
      this.syncAnswerStream();
      this.emitStatus();
    });
  }

  private syncPresenceActivity(): void {
    if (this.canAcknowledge() && this.primaryState === 'authenticated' && !this.idleSuspended) this.presence?.start();
    else this.presence?.stop();
    this.syncAnswerStream();
  }

  private canStreamAnswer(): boolean {
    return this.canAcknowledge() && this.primaryState === 'authenticated' && !this.idleSuspended &&
      (this.conversation?.status === 'ai' || this.conversation?.status === 'waiting');
  }

  private stopAnswerStream(): void {
    this.answerController?.abort();
    this.answerController = null;
    if (this.answerRetry !== null) clearTimeout(this.answerRetry);
    this.answerRetry = null;
    this.answerValue = null;
    this.answerFailures = 0;
    this.answerLive = false;
    this.answerCompletionCheck = null;
  }

  private syncAnswerStream(): void {
    if (!this.canStreamAnswer()) {
      this.stopAnswerStream();
      return;
    }
    if (!this.aiRespondingValue || this.answerDenied || !this.api.streamAnswer || this.answerController || this.answerRetry !== null) return;
    const epoch = this.epoch;
    const conversation = this.conversation;
    if (!conversation) return;
    const controller = new AbortController();
    this.answerController = controller;
    // 会话详情刷新会替换对象，因此隔离以 ID 与会话 session 身份为准。
    const session = this.conversationSession;
    const valid = (): boolean => this.isCurrent(epoch) && this.conversation?.id === conversation.id && this.conversationSession === session && this.answerController === controller;
    const timeout = setTimeout(() => controller.abort(), 35_000);
    void this.api.streamAnswer(conversation.id, controller.signal, (answer) => {
      if (!valid() || controller.signal.aborted) return;
      this.answerFailures = 0;
      this.answerLive = answer.responding;
      this.answerValue = answer;
      this.aiRespondingValue = answer.responding;
      this.emitStatus();
      if (!answer.responding) {
        this.stopAnswerStream();
        // 预览从不伪装成历史消息。完成、取消、转人工都由服务端历史收敛。
        void this.reconcile(epoch);
      }
    }).catch((error: unknown) => {
      if (!valid()) return;
      const kind = toSupportError(error).kind;
      if (kind === 'unauthorized' || kind === 'forbidden' || kind === 'not-found') {
        this.answerDenied = true;
        this.stopAnswerStream();
        this.aiRespondingValue = false;
        this.emitStatus();
      } else {
        this.answerFailures += 1;
      }
    }).finally(() => {
      clearTimeout(timeout);
      if (!valid()) return;
      this.answerController = null;
      this.answerLive = false;
      void this.checkAnswerCompletion();
      if (!this.canStreamAnswer() || !this.aiRespondingValue || this.answerDenied) return;
      this.answerRetry = setTimeout(() => {
        this.answerRetry = null;
        this.syncAnswerStream();
      }, Math.min(1000 * 2 ** Math.min(this.answerFailures, 4), 15_000));
    });
  }

  /** 断流后的预览已失效；用成功的 presence 响应确认完成，而不是把轮询失败的 EMPTY 当完成。 */
  private async checkAnswerCompletion(): Promise<void> {
    if (this.answerLive || this.answerCompletionCheck || !this.canStreamAnswer() || !this.aiRespondingValue) return;
    const conversationId = this.conversation?.id;
    if (!conversationId) return;
    const epoch = this.epoch;
    const session = this.conversationSession;
    const answer = this.answerValue;
    const check = {};
    this.answerCompletionCheck = check;
    try {
      const state = await this.api.request<PresenceState>(`/conversations/${encodeURIComponent(conversationId)}/presence`);
      if (this.answerCompletionCheck !== check || !this.isCurrent(epoch) || this.conversationSession !== session ||
        this.answerValue !== answer || this.answerLive || !this.canStreamAnswer()) return;
      if (state.aiResponding !== false) return;
      this.stopAnswerStream();
      this.aiRespondingValue = false;
      this.aiStatusValue = '';
      this.emitStatus();
      // 最终消息可能存在，也可能取消/转人工后没有 AI 消息，均以补拉结果为准。
      await this.reconcile(epoch);
    } catch {
      // presence 同样不可达时保留预览并继续重连，不能凭网络错误宣布回复完成。
    } finally {
      if (this.answerCompletionCheck === check) this.answerCompletionCheck = null;
    }
  }

  private emitPending(): void {
    this.emitter.emit('pending', this.pendingSends());
  }

  private beginIdentity(): number {
    this.epoch += 1;
    this.teardown();
    this.setState('authenticating');
    return this.epoch;
  }

  private isCurrent(epoch: number): boolean {
    return this.epoch === epoch;
  }

  private draftKey(): string {
    return this.conversation?.id ?? 'pending';
  }

  private emitEvent(payload: WidgetEventPayload): void {
    this.emitter.emit('event', payload);
  }

  private emitStatus(): void {
    this.emitter.emit('status', this.status);
  }

  private startQueuePolling(epoch: number): void {
    if (!this.isCurrent(epoch) || this.conversation?.status !== 'waiting' || this.queuePollTimer !== null) return;
    if (!this.visibleMessages().some((message) => message.senderType === 'customer' || message.senderType === 'system')) return;
    void this.refreshQueueStatus(epoch);
    this.queuePollTimer = setInterval(() => { void this.refreshQueueStatus(epoch); }, 3000);
  }

  /** 停止排队轮询并清空排队提示。 */
  private stopQueuePolling(): void {
    if (this.queuePollTimer !== null) clearInterval(this.queuePollTimer);
    this.queuePollTimer = null;
    this.queueCountValue = 0;
    this.allAgentsFullValue = false;
    this.queuePositionValue = 0;
    this.estimatedWaitSecondsValue = 0;
    this.offHoursValue = false;
    this.serviceHoursValue = '';
  }

  private async refreshQueueStatus(epoch: number): Promise<void> {
    const id = this.conversation?.id;
    if (!id || !this.isCurrent(epoch)) return;
    try {
      const queue = await this.api.request<{
        allAgentsFull: boolean;
        estimatedWaitSeconds?: number;
        offHours?: boolean;
        position?: number;
        serviceHours?: string;
        status: Conversation['status'];
        waitingCount: number;
      }>(
        `/conversations/${encodeURIComponent(id)}/queue`,
      );
      if (!this.isCurrent(epoch)) return;
      if (this.conversation?.id !== id || this.conversation.status !== 'waiting') return;
      this.queueCountValue = queue.waitingCount;
      this.allAgentsFullValue = queue.allAgentsFull;
      this.queuePositionValue = Number.isInteger(queue.position) ? Number(queue.position) : 0;
      this.estimatedWaitSecondsValue = Number.isInteger(queue.estimatedWaitSeconds) ? Number(queue.estimatedWaitSeconds) : 0;
      this.offHoursValue = queue.offHours === true;
      this.serviceHoursValue = typeof queue.serviceHours === 'string' ? queue.serviceHours : '';
      if (this.conversation) this.conversation.status = queue.status;
      if (queue.status !== 'waiting') this.stopQueuePolling();
      this.emitStatus();
    } catch {
      // 排队提示是附加信息；短暂轮询失败不能阻断消息收发。
    }
  }

  /**
   * 进入错误态：errorKind/errorMessage 与 state='error' 必须一起生效，
   * 否则 UI 会看到"有错误原因却还显示正常"的矛盾状态。
   */
  private fail(errorKind: string, errorMessage: string): void {
    this.errorKindValue = errorKind;
    this.errorMessageValue = errorMessage;
    this.setState('error');
  }

  private canAcknowledge(): boolean {
    return this.openValue && document.visibilityState === 'visible';
  }

  private async markReadIfPossible(): Promise<void> {
    if (!this.canAcknowledge()) return;
    const epoch = this.epoch;
    const session = this.conversationSession;
    try {
      const cursor = await session?.markRead();
      if (!this.isCurrent(epoch) || session !== this.conversationSession || !this.canAcknowledge()) {
        return;
      }
      if (cursor !== null && cursor !== undefined) {
        this.unreadValue = 0;
        this.emitStatus();
      }
    } catch (error) {
      // 已读推进失败不影响收发；下一次 ready 对齐时重试
      void error;
    }
  }

  /**
   * 把当前上下文上报服务端（POST /conversations/:id/context，尽力而为、可合并）。
   *
   * 三条硬口径：
   * - 没有会话（未认证/身份已切换）时绝不上报，也绝不说"已被服务端接收"；
   * - accepted 只能取自服务端应答；请求失败就不发带 accepted 的事件（不伪造）；
   * - 合并守卫必须**先落位句柄再启动异步体**：否则脏标记为空时异步体会同步跑完
   *   并把 null 写回，随后赋值留下永久"在飞"句柄 → 之后所有上报被静默吞掉。
   */
  private scheduleContextPush(): void {
    // 当前身份还没收到绑定的真实宿主上下文时不上报：
    // 绝不把 widget 内部占位页（ledger-widget）当成宿主页面落库。
    if (this.hostPageInstanceId === null || this.conversationSession === null) return;
    if (this.contextPushInFlight !== null) {
      this.contextPushDirty = true;
      return;
    }
    let resolve: () => void = () => {};
    const promise = new Promise<void>((settle) => {
      resolve = settle;
    });
    const handle = { promise, resolve };
    this.contextPushInFlight = handle;
    void this.drainContextPush(handle);
  }

  private async drainContextPush(handle: {
    promise: Promise<void>;
    resolve: () => void;
  }): Promise<void> {
    const epoch = this.epoch;
    try {
      do {
        this.contextPushDirty = false;
        const session = this.conversationSession;
        if (session === null || !this.isCurrent(epoch)) return;
        // 发送/上报时原子捕获：两个在途上报不会各自重新采集字段
        const snapshot = this.contextState.captureSnapshot();
        try {
          const accepted = await session.pushContext(snapshot);
          if (!this.isCurrent(epoch)) return;
          this.emitEvent({ accepted, kind: 'context.applied' });
        } catch {
          // 上下文上报失败：既不伪造 accepted，也不把发送通路拖进错误态
        }
      } while (this.contextPushDirty && this.isCurrent(epoch));
    } finally {
      if (this.contextPushInFlight === handle) this.contextPushInFlight = null;
      handle.resolve();
    }
  }

  /** 实时对齐：消息不可见时只更新未读，可见后才上报已读（epoch 防护）。 */
  private async reconcile(epoch: number): Promise<void> {
    if (!this.isCurrent(epoch)) return;
    const session = this.conversationSession;
    if (!session) return;
    try {
      await this.refreshConversationStatus(epoch);
      if (!this.isCurrent(epoch) || session !== this.conversationSession) return;
      // 空会话尚无 after 游标，第一条系统/Agent 消息必须重新取首屏。
      const added = session.visibleMessages().length === 0
        ? await session.loadInitial()
        : await session.loadNewer();
      if (!this.isCurrent(epoch) || session !== this.conversationSession) return;
      if (this.canAcknowledge()) {
        await session.markRead();
        if (!this.isCurrent(epoch) || session !== this.conversationSession) return;
        if (this.canAcknowledge()) this.unreadValue = 0;
      }
      if (this.primaryState === 'error') this.setState('authenticated');
      else this.emitStatus();
      // 收到新消息算作对话，重新开始计算空闲时间。
      if (added.length > 0 && this.realtime !== null) this.armIdle(epoch);
      for (const message of added) {
        this.emitEvent({
          conversationId: this.conversation?.id,
          kind: 'message.received',
          messageId: message.id,
          sequence: message.sequence,
        });
      }
      void this.presence?.refresh();
    } catch (error) {
      if (!this.isCurrent(epoch)) return;
      this.fail('sync', toSupportError(error).message);
    }
  }

  private async refreshConversationStatus(epoch: number): Promise<void> {
    const current = this.conversation;
    if (!current || !this.isCurrent(epoch)) return;
    try {
      const updated = await this.api.getConversation(current.id);
      if (!this.isCurrent(epoch) || this.conversation?.id !== current.id) return;
      this.applyConversationUpdate(updated);
    } catch {
      // 短暂的状态查询失败不阻断消息补拉；下次实时通知或打开窗口重试。
    }
  }

  private applyConversationUpdate(updated: Conversation): void {
    if (!this.conversation || this.conversation.id !== updated.id) return;
    const statusOrder: Record<Conversation['status'], number> = { ai: 0, waiting: 1, active: 2, closed: 3 };
    if (statusOrder[updated.status] < statusOrder[this.conversation.status]) return;
    this.conversation = updated;
    if (updated.status === 'closed') this.presence?.clearTyping();
    if (updated.status !== 'ai' && updated.status !== 'waiting') this.aiRespondingValue = false;
    if (!this.canAcknowledge()) this.unreadValue = updated.unread;
    if (updated.status !== 'waiting') this.stopQueuePolling();
    this.noteWatchHint();
    this.syncAnswerStream();
    this.emitStatus();
    // 申请、同意、进入、结束都会写系统消息并更新会话时间；授权中再按时检查到期。
    if (updated.updatedAt !== this.assistCheckedAt || isAccountAssistOpen(this.assistValue)) void this.refreshAssist(this.epoch);
  }

  private async fetchAssist(conversationId: string): Promise<AccountAssist | null> {
    const result = await this.api.request<{ assist: AccountAssist | null }>(
      `/conversations/${encodeURIComponent(conversationId)}/assist`,
    );
    const assist = result?.assist;
    return assist && assist.conversationId === conversationId ? assist : null;
  }

  /** 读当前会话的账号协助；状态有变化时通知界面。 */
  private async refreshAssist(epoch: number): Promise<void> {
    const current = this.conversation;
    if (!current || !this.isCurrent(epoch) || this.store.token === null) return;
    const checkedAt = current.updatedAt;
    try {
      const assist = await this.fetchAssist(current.id);
      if (!this.isCurrent(epoch) || this.conversation?.id !== current.id) return;
      this.assistCheckedAt = checkedAt;
      this.setAssist(isAccountAssistOpen(assist) ? assist : null);
    } catch {
      // 读不到时保持原状态，下次会话更新或检查时再试。
    }
  }

  private setAssist(next: AccountAssist | null): void {
    const prev = this.assistValue;
    this.assistValue = next;
    if (prev?.id === next?.id && prev?.status === next?.status && prev?.expiresAt === next?.expiresAt &&
      prev?.openedAt === next?.openedAt) return;
    this.noteWatchHint();
    this.emitter.emit('assist', next);
  }

  /** 客户同意、拒绝或取消客服登录自己账号的授权。 */
  async actAssist(assistId: string, action: Exclude<AccountAssistAction, 'end'>): Promise<AccountAssist> {
    const current = this.conversation;
    if (!current || this.assistValue?.id !== assistId) throw new Error('授权申请已变化，请刷新后重试');
    const epoch = this.epoch;
    await this.ensureToken(epoch);
    if (!this.isCurrent(epoch)) throw new Error('身份已变化');
    const updated = await this.api.request<AccountAssist>(`/assist/${encodeURIComponent(assistId)}/actions`, {
      body: { action },
      method: 'POST',
    });
    if (this.isCurrent(epoch) && this.conversation?.id === current.id) {
      this.setAssist(isAccountAssistOpen(updated) ? updated : null);
      if (this.isLive()) void this.reconcile(epoch);
    }
    return updated;
  }

  private async resolveConversation(): Promise<Conversation> {
    const page = await this.api.listConversations({ limit: 1 });
    const latest = page.items[0];
    if (latest) return latest;
    // 首次咨询才自动创建；已结束的会话由客户明确继续。
    return await this.api.createConversation(newRequestId());
  }

  private setState(state: WidgetSessionState): void {
    this.primaryState = state;
    if (state !== 'error') {
      this.errorKindValue = undefined;
      this.errorMessageValue = undefined;
    }
    this.emitStatus();
  }

  private async startRealtime(epoch: number): Promise<void> {
    if (!this.isCurrent(epoch)) return;
    const realtime = this.createRealtime({
      onAuthExpired: () => {
        if (!this.isCurrent(epoch)) return;
        this.fail('auth', '实时连接凭据已过期，需要重新登录');
      },
      onReady: () => this.reconcile(epoch),
      onStatusChange: (status) => {
        if (!this.isCurrent(epoch)) return;
        if (status === 'connecting') this.startFallbackPoll(epoch);
        else this.stopFallbackPoll();
        this.emitStatus();
      },
      onSyncRequired: () => this.reconcile(epoch),
    });
    this.realtime = realtime;
    try {
      await realtime.start();
    } catch (error) {
      if (!this.isCurrent(epoch)) return;
      // 实时不可用不影响 HTTP 收发：明确暴露为错误，但不虚假宣称"已连接"
      this.fail('realtime', toSupportError(error).message);
    }
  }

  private startFallbackPoll(epoch: number): void {
    if (this.fallbackPollTimer !== null) return;
    this.fallbackPollTimer = setInterval(() => {
      if (!this.isCurrent(epoch)) {
        this.stopFallbackPoll();
        return;
      }
      void this.reconcile(epoch);
    }, FALLBACK_POLL_MS);
  }

  private stopFallbackPoll(): void {
    if (this.fallbackPollTimer !== null) clearInterval(this.fallbackPollTimer);
    this.fallbackPollTimer = null;
  }

  /**
   * 身份切换清理：关 WS、丢弃会话（其上的在飞写入自然失效）、清令牌/草稿/
   * 消息/错误，并把上下文重置为"未核实"（账套/账期显式为空）。
   *
   * 宿主绑定与推送状态同样按身份隔离：解绑宿主实例、清空版本台账与本地字段，
   * 作废在飞的上下文上报——新身份在收到属于它的真实 host.context 前绝不上报，
   * 且旧 epoch 的在飞上报不会挡住新身份的上报。
   */
  private teardown(): void {
    this.stopWatch();
    this.clearIdle();
    this.idleSuspended = false;
    this.connecting = null;
    this.stopAnswerStream();
    this.answerDenied = false;
    this.stopFallbackPoll();
    this.presence?.stop();
    this.presence = null;
    this.counterpartTypingValue = false;
    this.aiRespondingValue = false;
    this.stopQueuePolling();
    const realtime = this.realtime;
    this.realtime = null;
    try {
      realtime?.stop();
    } catch {
      // 关闭失败不影响状态收敛
    }
    this.conversationSession = null;
    this.conversation = null;
    this.assistCheckedAt = '';
    if (this.assistValue !== null) {
      // 换身份或退出：旧身份的横幅立即撤掉。
      this.assistValue = null;
      this.emitter.emit('assist', null);
    }
    this.unreadValue = 0;
    this.hostPageInstanceId = null;
    this.hostVersions.clear();
    this.localFields = {};
    this.contextPushDirty = false;
    this.contextPushInFlight = null;
    this.store.clear();
    this.drafts.clear();
    this.contextState.reset({ pageKey: this.initialContext.pageKey });
    this.errorKindValue = undefined;
    this.errorMessageValue = undefined;
  }

  /**
   * 本地字段补在宿主字段之上：宿主提供的键**一律优先**（本地无权改写宿主权威口径），
   * 本地字段只补宿主没给的键；pageKey（页面标识）永远由宿主拥有。
   */
  private overlayLocalFields(fields: SupportPageContextFields): SupportPageContextFields {
    const merged: SupportPageContextFields = { ...fields };
    for (const [key, value] of Object.entries(this.localFields)) {
      if (value === undefined || key === 'pageKey') continue;
      if (key in merged) continue;
      (merged as Record<string, unknown>)[key] = value;
    }
    return merged;
  }
}

function readWatchHint(): boolean {
  try {
    return Number(globalThis.localStorage?.getItem(WATCH_HINT_KEY)) > Date.now();
  } catch {
    return false;
  }
}

function writeWatchHint(): void {
  try {
    globalThis.localStorage?.setItem(WATCH_HINT_KEY, String(Date.now() + WATCH_HINT_TTL_MS));
  } catch {
    // 浏览器禁用存储时只是刷新后不再提醒，不影响咨询。
  }
}

function clearWatchHint(): void {
  try {
    globalThis.localStorage?.removeItem(WATCH_HINT_KEY);
  } catch {
    // 同上
  }
}

function toProtocolState(state: WidgetSessionState): WidgetStatusPayload['state'] {
  // 握手中/退出这类本端状态协议里只有 idle 可表达，绝不借用 authenticated
  return state === 'authenticating' ? 'idle' : state;
}

function toPrincipalRef(principal: {
  id: string;
  name: string;
  orgId: string;
  role: 'admin' | 'agent' | 'customer' | 'supervisor';
  scopeId: string;
}): SupportPrincipalRef {
  return {
    id: principal.id,
    name: principal.name,
    orgId: principal.orgId,
    role: principal.role,
    scopeId: principal.scopeId,
  };
}

function reasonForError(
  kind: string,
): 'internal' | 'schema' | 'unauthorized' {
  if (kind === 'unauthorized' || kind === 'forbidden') return 'unauthorized';
  if (kind === 'protocol' || kind === 'invalid') return 'schema';
  return 'internal';
}

/** 只保留协议允许的页面字段，其余（含 companyVerified）一律丢弃。 */
function contextFields(context: SupportPageContext): SupportPageContextFields {
  const fields: SupportPageContextFields = { pageKey: context.pageKey };
  if (context.companyId !== undefined) fields.companyId = context.companyId;
  if (context.period !== undefined) fields.period = context.period;
  if (context.filters !== undefined) fields.filters = { ...context.filters };
  for (const key of [
    'frontendVersion',
    'lastErrorCode',
    'module',
    'pageTitle',
    'product',
    'routeName',
    'selectedObjectId',
    'tab',
    'traceId',
  ] as const) {
    const value = context[key];
    if (value === undefined) continue;
    (fields as Record<string, unknown>)[key] = value;
  }
  return fields;
}

/**
 * 从宿主帧取出可用的页面字段；没有可用页面标识时返回 null（该帧必须被忽略）。
 * 不静默兜底成 widget 自己的 pageKey/占位页：那不是宿主上报的页面。
 */
function hostContextFields(payload: HostContextPayload): null | SupportPageContextFields {
  const context: unknown = payload.context;
  if (typeof context !== 'object' || context === null) return null;
  const fields = contextFields(context as SupportPageContext);
  if (typeof fields.pageKey !== 'string' || fields.pageKey === '') return null;
  return fields;
}
